import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Phone, ShieldCheck } from "lucide-react";

export default function CourierOtpDialog({ courierId, onClose, onHandoverComplete }) {
  const [step, setStep] = useState("mobile");
  const [form, setForm] = useState({ mobile: "", otp: "", recipient_name: "" });
  const [loading, setLoading] = useState(false);

  const handleSendOtp = async () => {
    if (!form.mobile?.trim()) { toast.error("Enter recipient mobile number"); return; }
    setLoading(true);
    try {
      await api.post(`/mod6/courier/${courierId}/send-handover-otp`, { recipient_mobile: form.mobile });
      toast.success("OTP sent to recipient");
      setStep("otp");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send OTP"); }
    setLoading(false);
  };

  const handleVerifyOtp = async () => {
    if (!form.otp?.trim()) { toast.error("Enter OTP"); return; }
    setLoading(true);
    try {
      await api.post(`/mod6/courier/${courierId}/verify-handover-otp`, { otp: form.otp });
      toast.success("OTP verified");
      setStep("confirm");
    } catch (err) { toast.error(err.response?.data?.detail || "Invalid OTP"); }
    setLoading(false);
  };

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await api.put(`/mod6/courier/${courierId}/handover`, {
        handed_over_to: form.recipient_name || "Recipient",
        recipient_mobile: form.mobile,
      });
      toast.success("Courier handed over successfully");
      onHandoverComplete();
    } catch (err) { toast.error(err.response?.data?.detail || "Handover failed"); }
    setLoading(false);
  };

  return (
    <Dialog open={!!courierId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Chivo" }} className="flex items-center gap-2">
            <ShieldCheck size={18} className="text-emerald-600" /> Courier Handover — OTP Verification
          </DialogTitle>
        </DialogHeader>
        {step === "mobile" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">Enter recipient&apos;s mobile number to send OTP for handover verification.</p>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Recipient Name</Label>
              <Input value={form.recipient_name} onChange={e => setForm(p => ({ ...p, recipient_name: e.target.value }))} className="h-11" placeholder="Recipient name" data-testid="courier-recipient-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Recipient Mobile *</Label>
              <div className="flex items-center gap-2">
                <Phone size={16} className="text-gray-400" />
                <Input value={form.mobile} onChange={e => setForm(p => ({ ...p, mobile: e.target.value }))} className="h-11 flex-1" placeholder="+91 XXXXX XXXXX" type="tel" data-testid="courier-recipient-mobile" />
              </div>
            </div>
            <Button onClick={handleSendOtp} disabled={loading} className="w-full bg-[#1B3C87] hover:bg-[#2650A8] h-11" data-testid="send-courier-otp-btn">
              {loading ? "Sending..." : "Send OTP"}
            </Button>
          </div>
        )}
        {step === "otp" && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">OTP has been sent to <strong>{form.mobile}</strong>. Ask the recipient to share the code.</p>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider font-bold text-gray-500">Enter OTP *</Label>
              <Input value={form.otp} onChange={e => setForm(p => ({ ...p, otp: e.target.value }))} className="h-11 text-center text-lg tracking-[0.5em] font-mono" maxLength={6} placeholder="------" data-testid="courier-otp-input" />
            </div>
            <Button onClick={handleVerifyOtp} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="verify-courier-otp-btn">
              {loading ? "Verifying..." : "Verify OTP"}
            </Button>
            <Button variant="ghost" className="w-full text-xs text-gray-500" onClick={() => setStep("mobile")}>
              Change number / Resend
            </Button>
          </div>
        )}
        {step === "confirm" && (
          <div className="space-y-4">
            <div className="text-center py-2">
              <ShieldCheck size={36} className="mx-auto text-emerald-500 mb-2" />
              <p className="text-sm font-semibold text-emerald-700">OTP Verified Successfully</p>
              <p className="text-xs text-gray-500 mt-1">Recipient: {form.recipient_name || "—"} ({form.mobile})</p>
            </div>
            <Button onClick={handleConfirm} disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="confirm-courier-handover-btn">
              {loading ? "Processing..." : "Confirm Handover"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
